import React from 'react';
import PropTypes from 'prop-types';
import { renderRoutes } from 'react-router-config';
import { Redirect } from 'react-router-dom';
import { useStore } from 'effector-react';
import { Trans, t } from '@lingui/macro';

import { Button } from '@oca/ui';
import { CommonPageTemplate, PageLoader } from '@features/common';
import { i18n } from '@lib/i18n';
import { notify } from '@lib/notifier';

import {
  $isSessionReady,
  $sessionRequest,
  $currentWorkspaceId,
  startSessionFx,
} from '../model/session';
import { loadReferenceDataFx } from '../model/reference';
import {
  Card,
  EmptyState,
  ErrorBanner,
  CreateWorkspaceDialog,
} from '../molecules';
import { errorMessage } from '../lib/format';

/**
 * Parent of every /recordflow route. Signs in to RecordFlow through OCA SSO,
 * requires a workspace, and remounts the pages when the workspace changes so
 * each one loads that workspace's data.
 */
export function RecordflowMainPage({ route, match }) {
  const isReady = useStore($isSessionReady);
  const { isLoading, error } = useStore($sessionRequest);
  const workspaceId = useStore($currentWorkspaceId);
  const [isCreateOpen, setCreateOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isReady) startSessionFx().catch(() => {});
  }, [isReady]);

  React.useEffect(() => {
    if (isReady && workspaceId) {
      loadReferenceDataFx().catch(failure =>
        notify.error(errorMessage(failure)),
      );
    }
  }, [isReady, workspaceId]);

  if (error && !isLoading) {
    return (
      <CommonPageTemplate heading={i18n._(t`RecordFlow`)} contentWidth={1}>
        <Card>
          <EmptyState title={<Trans>Could not open RecordFlow</Trans>}>
            <ErrorBanner error={error} />
            <Button
              variant="primary"
              onClick={() => startSessionFx().catch(() => {})}
            >
              <Trans>Try again</Trans>
            </Button>
          </EmptyState>
        </Card>
      </CommonPageTemplate>
    );
  }

  if (!isReady) return <PageLoader />;

  if (!workspaceId) {
    return (
      <CommonPageTemplate heading={i18n._(t`RecordFlow`)} contentWidth={1}>
        <Card>
          <EmptyState
            title={<Trans>You have no RecordFlow workspace yet</Trans>}
          >
            <p>
              <Trans>
                A workspace holds your documents, transactions and settings.
              </Trans>
            </p>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Trans>Create workspace</Trans>
            </Button>
          </EmptyState>
        </Card>
        {isCreateOpen && (
          <CreateWorkspaceDialog onClose={() => setCreateOpen(false)} />
        )}
      </CommonPageTemplate>
    );
  }

  return (
    <React.Fragment key={workspaceId}>
      {match.isExact && <Redirect to={route.routes[0].path} />}
      {renderRoutes(route.routes)}
    </React.Fragment>
  );
}

RecordflowMainPage.propTypes = {
  route: PropTypes.shape({
    routes: PropTypes.array,
  }).isRequired,
  match: PropTypes.shape({
    isExact: PropTypes.bool,
  }).isRequired,
};
